import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "@/api/client";
import type { ResourceType, Unit } from "@/api/types";
import { useStore } from "@/store/StoreContext";
import { Badge, Button, Drawer, Field, Input, tx } from "@/components/ui";

/** Seats & machines: one row per resource type, its units listed underneath; the row opens the editor. */
export function ResourcesPage() {
  const { store } = useStore();
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<ResourceType | "new" | null>(null);
  const types = useQuery({ queryKey: ["resource-types", store?.id], queryFn: () => api<ResourceType[]>(`/app/resource-types?store_id=${store!.id}`), enabled: !!store });
  if (!store) return null;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between"><h1 className="text-2xl font-semibold">{t("nav.resources")}</h1><Button onClick={() => setEditing("new")}>{t("resources.new")}</Button></div>
      <table className="w-full overflow-hidden rounded-xl border bg-white text-sm">
        <thead className="bg-gray-50 text-left text-xs text-gray-500"><tr><th className="p-2">{t("resources.type")}</th><th className="p-2">key</th><th className="p-2">{t("resources.units")}</th><th className="p-2 text-right">{t("resources.count")}</th><th className="p-2"></th></tr></thead>
        <tbody>
          {types.data?.map((r) => (
            <tr key={r.id} className="cursor-pointer border-t hover:bg-gray-50" onClick={() => setEditing(r)}>
              <td className="p-2 font-medium">{tx(r.name, i18n.language)}</td>
              <td className="p-2 font-mono text-xs text-gray-500">{r.key}</td>
              <td className="p-2"><div className="flex flex-wrap gap-1">{r.units.map((u) => <Badge key={u.id} tone={u.active ? "blue" : "gray"}>{u.name}</Badge>)}</div></td>
              <td className="p-2 text-right">{r.units.filter((u) => u.active).length}/{r.units.length}</td>
              <td className="p-2 text-right"><button type="button" className="text-gray-500 hover:text-gray-800" title={t("app.edit")} onClick={(e) => { e.stopPropagation(); setEditing(r); }}>✎</button></td>
            </tr>
          ))}
          {types.data?.length === 0 && <tr><td className="p-2 text-gray-500" colSpan={5}>{t("resources.none")}</td></tr>}
        </tbody>
      </table>
      <Drawer open={!!editing} onClose={() => setEditing(null)} title={editing === "new" ? t("resources.new") : t("app.edit")}>
        {editing && <TypeForm storeId={store.id} type={editing === "new" ? null : editing} onDone={() => { qc.invalidateQueries({ queryKey: ["resource-types"] }); qc.invalidateQueries({ queryKey: ["calendar"] }); setEditing(null); }} />}
      </Drawer>
    </div>
  );
}

function TypeForm({ storeId, type, onDone }: { storeId: string; type: ResourceType | null; onDone: () => void }) {
  const { t } = useTranslation();
  const [key, setKey] = useState(type?.key ?? "");
  const [nameEl, setNameEl] = useState(type?.name.el ?? "");
  const [nameEn, setNameEn] = useState(type?.name.en ?? "");
  const [units, setUnits] = useState<Unit[]>(type?.units ?? [{ name: "", active: true, sort: 0, duo_pair_with: null }]);
  const [error, setError] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () => {
      const body = { name: { el: nameEl, en: nameEn || nameEl }, units: units.filter((u) => u.name.trim()).map((u, i) => ({ ...u, sort: i })) };
      return type ? api(`/app/resource-types/${type.id}`, { method: "PATCH", body: JSON.stringify(body) })
        : api("/app/resource-types", { method: "POST", body: JSON.stringify({ store_id: storeId, key, ...body }) });
    },
    onSuccess: onDone, onError: (e) => setError(e instanceof ApiError ? e.detail : t("app.error")),
  });
  const submit = (e: FormEvent) => { e.preventDefault(); m.mutate(); };
  const setU = (i: number, patch: Partial<Unit>) => setUnits(units.map((u, j) => (j === i ? { ...u, ...patch } : u)));
  return (
    <form onSubmit={submit} className="space-y-3 text-sm">
      <div className="grid gap-2 sm:grid-cols-3">
        <Field label="key" hint="a-z, 0-9, _"><Input value={key} onChange={(e) => setKey(e.target.value)} pattern="[a-z0-9_]{2,40}" required disabled={!!type} /></Field>
        <Field label="Όνομα (EL)"><Input value={nameEl} onChange={(e) => setNameEl(e.target.value)} required /></Field>
        <Field label="Name (EN)"><Input value={nameEn} onChange={(e) => setNameEn(e.target.value)} /></Field>
      </div>
      <div className="space-y-1">
        <div className="font-medium">{t("resources.units")}</div>
        {units.map((u, i) => (
          <div key={u.id ?? i} className="grid grid-cols-[1fr_auto_auto] items-center gap-2">
            <Input value={u.name} placeholder={t("resources.unitName")} onChange={(e) => setU(i, { name: e.target.value })} />
            <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={u.active} onChange={(e) => setU(i, { active: e.target.checked })} /> {t("app.active")}</label>
            <button type="button" className="text-xs text-gray-500 hover:text-red-600" disabled={units.length === 1} onClick={() => setUnits(units.filter((_, j) => j !== i))}>✕</button>
          </div>
        ))}
        <Button type="button" variant="ghost" onClick={() => setUnits([...units, { name: "", active: true, sort: units.length, duo_pair_with: null }])}>{t("resources.addUnit")}</Button>
      </div>
      {error && <p className="text-red-600">{error}</p>}
      <Button disabled={m.isPending}>{t("app.save")}</Button>
    </form>
  );
}
